import React, { memo, useEffect, useRef } from 'react';
import { Animated, StyleSheet, TouchableOpacity, View, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Text } from '../ui/AppText';
import { Image } from 'expo-image';
import Svg, { Path } from 'react-native-svg';

const SWIRL_PATH = 'M 100 64.286 C 119.725 64.286 135.714 80.276 135.714 100 C 135.714 132.482 126.367 165.386 99.897 194.125 C 97.224 197.027 92.705 197.212 89.804 194.54 C 86.902 191.867 86.716 187.348 89.389 184.447 C 113.034 158.774 121.429 129.226 121.429 100 C 121.429 88.165 111.835 78.571 100 78.571 C 88.165 78.571 78.571 88.165 78.571 100 C 78.571 124.875 68.849 148.902 43.977 170.565 C 41.002 173.156 36.49 172.845 33.899 169.87 C 31.308 166.895 31.62 162.383 34.594 159.792 C 55.72 141.392 63.942 121.778 64.275 101.308 L 64.286 100 C 64.286 80.275 80.276 64.286 100 64.286 Z M 100 92.857 C 103.945 92.857 107.143 96.055 107.143 100 C 107.143 129.193 94.774 161.756 69.337 187.194 C 66.547 189.983 62.024 189.983 59.235 187.194 C 56.446 184.404 56.446 179.882 59.235 177.092 C 81.878 154.449 92.857 125.545 92.857 100 C 92.857 96.055 96.055 92.857 100 92.857 Z M 100 32.143 C 137.477 32.143 167.857 62.524 167.857 100 C 167.857 129.852 162.461 146.407 144.782 181.766 C 143.017 185.294 138.727 186.724 135.198 184.96 C 131.67 183.196 130.24 178.905 132.004 175.377 L 133.922 171.516 L 135.807 167.663 C 149.512 139.399 153.571 124.947 153.571 100 C 153.571 70.413 129.587 46.429 100 46.429 C 70.788 46.429 47.037 69.81 46.44 98.879 L 46.429 100 C 46.429 117.441 39.965 132.41 27.234 144.471 C 24.37 147.184 19.849 147.062 17.136 144.198 C 14.423 141.334 14.545 136.813 17.409 134.1 C 26.956 125.055 31.803 114.283 32.126 101.389 L 32.143 100 C 32.143 62.523 62.524 32.143 100 32.143 Z M 100 0 C 155.229 0 200 44.771 200 100 C 200 106.603 199.359 113.127 198.097 119.508 C 196.825 125.937 194.92 132.225 192.41 138.279 C 190.899 141.923 186.72 143.652 183.076 142.141 C 179.432 140.63 177.703 136.451 179.214 132.807 C 181.363 127.624 182.994 122.24 184.083 116.735 C 185.165 111.267 185.714 105.671 185.714 100 C 185.714 52.661 147.339 14.286 100 14.286 C 52.661 14.286 14.286 52.661 14.286 100 C 14.286 103.945 11.088 107.143 7.143 107.143 C 3.198 107.143 0 103.945 0 100 C 0 44.771 44.771 0 100 0 Z';
const COMMUNITY_AVATARS = [
  'https://randomuser.me/api/portraits/women/44.jpg',
  'https://randomuser.me/api/portraits/men/32.jpg',
  'https://randomuser.me/api/portraits/women/68.jpg',
];

const InviteFriendsCard = memo(({ onPress, userPhoto }) => {
  // Ver nota em RedacaoEntryCard: loop rodando fora de foco trava a tela seguinte.
  const isFocused = useIsFocused();

  const badgePulse = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(badgePulse, {
          toValue: 1.14,
          duration: 850,
          useNativeDriver: Platform.OS !== 'web',
          isInteraction: false, // loop infinito NAO e interacao do usuario
        }),
        Animated.timing(badgePulse, {
          toValue: 1,
          duration: 850,
          useNativeDriver: Platform.OS !== 'web',
          isInteraction: false, // loop infinito NAO e interacao do usuario
        }),
      ])
    );

    if (isFocused) animation.start();
    return () => animation.stop();
  }, [badgePulse, isFocused]);

  return (
    <TouchableOpacity
      style={styles.card}
      activeOpacity={0.9}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Acesse a comunidade e compartilhe sua evolução"
    >
      <View style={styles.swirl} pointerEvents="none">
        <Svg width="100%" height="100%" viewBox="0 0 200 196.429">
          <Path d={SWIRL_PATH} fill="#343434" />
        </Svg>
      </View>

      <View style={styles.closeMark} pointerEvents="none">
        <Svg width={14} height={14} viewBox="0 0 14 14">
          <Path
            d="M 1.4 14 L 0 12.6 L 5.6 7 L 0 1.4 L 1.4 0 L 7 5.6 L 12.6 0 L 14 1.4 L 8.4 7 L 14 12.6 L 12.6 14 L 7 8.4 L 1.4 14 Z"
            fill="#FFFFFF"
          />
        </Svg>
      </View>

      <View style={styles.avatars} pointerEvents="none">
        <View style={[styles.avatar, styles.avatarLeft]}>
          <Image source={{ uri: COMMUNITY_AVATARS[0] }} style={styles.avatarImage} contentFit="cover" />
        </View>

        <View style={[styles.avatar, styles.avatarCenter]}>
          <Image source={{ uri: userPhoto || COMMUNITY_AVATARS[1] }} style={styles.avatarImage} contentFit="cover" />
        </View>

        <View style={[styles.avatar, styles.avatarRight]}>
          <Image source={{ uri: COMMUNITY_AVATARS[2] }} style={styles.avatarImage} contentFit="cover" />
        </View>
      </View>

      <View style={styles.body} pointerEvents="none">
        <Animated.View style={[styles.badge, { transform: [{ scale: badgePulse }] }]}>
          <Text style={styles.badgeText}>Clique aqui</Text>
        </Animated.View>
        <Text style={styles.title} numberOfLines={3}>
          Acesse a comunidade{'\n'}e compartilhe sua{'\n'}evolução
        </Text>
      </View>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    height: 238,
    borderRadius: 20,
    backgroundColor: '#000000',
    overflow: 'hidden',
    padding: 20,
    marginBottom: 16,
  },
  swirl: {
    position: 'absolute',
    top: -16,
    right: -36,
    width: 220,
    height: 220,
    transform: [{ rotate: '225deg' }],
    opacity: 1,
  },
  closeMark: {
    position: 'absolute',
    top: 20,
    left: 20,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  avatars: {
    position: 'absolute',
    top: 20,
    right: 30,
    width: 124,
    height: 68,
  },
  avatar: {
    position: 'absolute',
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: '#000000',
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarLeft: {
    top: 10,
    left: 0,
  },
  avatarCenter: {
    top: 0,
    left: 38,
    zIndex: 2,
  },
  avatarRight: {
    top: 10,
    left: 76,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  body: {
    position: 'absolute',
    left: 20,
    right: 20,
    bottom: 20,
    alignItems: 'center',
    gap: 10,
  },
  badge: {
    height: 24,
    paddingHorizontal: 10,
    borderRadius: 20,
    backgroundColor: 'rgb(252, 142, 26)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: '#000000',
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '700',
  },
  title: {
    width: '100%',
    height: 96,
    color: '#FFFFFF',
    fontSize: 32,
    lineHeight: 32,
    fontWeight: '900',
    textAlign: 'center',
  },
});

export default InviteFriendsCard;
